'use client'

import React, { useId } from 'react'
import { Box, Flex, Title } from '@mantine/core'
import { motion } from 'framer-motion'
import { useModal } from '@/hooks/useModal'
import parseHtml from '@/lib/parseHtml'
import ModalManager from '@/components/Refresh/Modals/ModalManager/ModalManager'
import { refreshTitleVariantProps } from '@/components/Refresh/RefreshTypography'
import { textFadeInVariantsNoTransition, staggerContainer } from '@/lib/animations'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import type { SkuComponentConfigMap } from '@/types/enums/sku-component-types'
import VideoPlaceholderImg from '@/assets/images/refresh/sku/video-placeholder.webp'
import { MediaShell } from './MediaShell'
import { NumberedSteps } from './NumberedSteps'
import { FeatureGrid } from './FeatureGrid'

type SkuVideoDataConfig = SkuComponentConfigMap['skuVideoData']

export function WhatIsSkuWidget({ config }: { config: SkuVideoDataConfig }) {
    const {
        title,
        content,
        mediaOverlayTitle,
        mediaOverlaySubtitle,
        backgroundVideoSrc,
        modalVideoUrl,
        mediaImageSrc,
        mediaImageAlt,
        useCardsAsContent,
        items,
    } = config
    const titleId = `sku-video-data-title-${useId()}`

    const videoModal = useModal()

    const hasBackgroundVideo = Boolean(backgroundVideoSrc)
    const modalSrc = modalVideoUrl ?? backgroundVideoSrc ?? ''
    const showPlayControl = hasBackgroundVideo && Boolean(modalSrc)
    const showNumberedStepsInTop = Boolean(useCardsAsContent && items.length > 0)
    const resolvedMediaImageSrc = mediaImageSrc ?? VideoPlaceholderImg

    return (
        <>
            <ModalManager type="video" {...videoModal} videoUrl={modalSrc} />
            <Box
                component={motion.section}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-100px 0px' }}
                variants={staggerContainer}
                aria-labelledby={titleId}
                maw={1440}
                mx="auto"
                w="100%"
                p={{ base: 24, sm: 36, md: 60 }}
                style={{
                    overflow: 'hidden',
                    backgroundColor: '#e8e6ed',
                    borderRadius: 24,
                }}
            >
                <Flex
                    direction={{ base: 'column', sm: 'row' }}
                    align="stretch"
                    gap={{ base: 24, sm: 32, lg: 48 }}
                    wrap="nowrap"
                    style={{ minWidth: 0 }}
                >
                    <Flex
                        direction="column"
                        gap={{ base: 16, sm: 20, md: 24 }}
                        miw={0}
                        style={{ flex: '1 1 0', minWidth: 0 }}
                    >
                        <MotionBox variants={textFadeInVariantsNoTransition}>
                            <Title {...refreshTitleVariantProps.section2xl} id={titleId} c="gray.9" maw={640}>
                                {title}
                            </Title>
                        </MotionBox>
                        {content ? (
                            <MotionBox variants={textFadeInVariantsNoTransition}>
                                <Box maw={640} c="gray.7" fz={16} fw={450} lh="150%">
                                    {parseHtml(content)}
                                </Box>
                            </MotionBox>
                        ) : null}

                        {showNumberedStepsInTop ? <NumberedSteps items={items} /> : null}
                    </Flex>

                    <MotionBox
                        variants={textFadeInVariantsNoTransition}
                        style={{
                            flex: '1 1 0',
                            minWidth: 0,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <MediaShell
                            backgroundVideoSrc={backgroundVideoSrc}
                            mediaImageSrc={resolvedMediaImageSrc}
                            mediaImageAlt={mediaImageAlt}
                            mediaOverlayTitle={mediaOverlayTitle}
                            mediaOverlaySubtitle={mediaOverlaySubtitle}
                            showPlayControl={showPlayControl}
                            useCardsAsContent={useCardsAsContent}
                            onPlayClick={() => videoModal.open()}
                        />
                    </MotionBox>
                </Flex>

                {items.length > 0 && !useCardsAsContent ? <FeatureGrid items={items} /> : null}
            </Box>
        </>
    )
}
